import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} panel
 *
 * @extends Component<Refs>
 */
class DevMobileMenu extends Component {
  requiredRefs = ['panel'];

  #previousScrollY = 0;
  #previousFocus = null;

  openMenu = () => {
    if (this.hasAttribute('open')) return;

    this.#previousScrollY = window.scrollY;
    this.#previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');

    document.body.setAttribute('data-mobile-menu-open', '');
    document.body.style.top = `-${this.#previousScrollY}px`;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';

    document.addEventListener('keydown', this.#handleKeyDown);

    const firstFocusable = this.refs.panel.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable instanceof HTMLElement) firstFocusable.focus({ preventScroll: true });
  };

  closeMenu = () => {
    if (!this.hasAttribute('open')) return;

    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');

    document.body.removeAttribute('data-mobile-menu-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    document.removeEventListener('keydown', this.#handleKeyDown);

    this.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));

    if (this.#previousFocus) this.#previousFocus.focus({ preventScroll: true });
  };

  toggleMenu = () => {
    if (this.hasAttribute('open')) this.closeMenu();
    else this.openMenu();
  };

  // Cart tile: open the cart drawer instead of navigating to /cart — mirrors
  // the desktop navbar's cart action. closeMenu first so the drawer isn't
  // stacked behind this full-screen menu (same reasoning as the Garage tile).
  // href stays a real cart link (not "#") as a no-JS fallback; preventDefault
  // only runs once we know we're taking over the click.
  openCart = (event) => {
    const drawer = document.getElementById('DevCartDrawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    this.closeMenu();
    drawer.open();
  };

  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu();
    }
  };
}

if (!customElements.get('dev-mobile-menu')) customElements.define('dev-mobile-menu', DevMobileMenu);
